/**
 * Header + footer for multi-country scheduled sent edit wizard.
 */

import type { FC } from 'react';
import { ArrowLeft, ArrowRight, Globe, Loader2, Save, X } from 'lucide-react';

import { Button } from '@/components/ui';
import { StepNav } from '@/features/smswiser-campaigns/components/MultiCountryBulkEdit/steps/StepNav';
import type { WizardStep } from '@/features/smswiser-campaigns/components/MultiCountryBulkEdit/hooks/useMultiCountryBulkEditForm';

import type { useMultiCountrySentScheduledEditForm } from '../hooks/useMultiCountrySentScheduledEditForm';

type Form = ReturnType<typeof useMultiCountrySentScheduledEditForm>;

interface HeaderProps {
  form: Form;
  affectedCount: number;
  onClose: () => void;
  isBusy: boolean;
}

export const SentScheduledWizardHeader: FC<HeaderProps> = ({ form, affectedCount, onClose, isBusy }) => (
  <div className="flex shrink-0 items-center justify-between gap-3 border-b border-border bg-muted/30 px-5 py-3">
    <div className="flex items-center gap-3">
      <Globe className="h-5 w-5 text-primary" />
      <div className="hidden sm:block">
        <h3 className="text-base font-semibold">Multi-country sent edit</h3>
        <p className="text-xs text-muted-foreground">
          {affectedCount} scheduled drops · {form.allCountryKeys.join(', ')}
        </p>
      </div>
    </div>
    <StepNav current={form.step} onChange={form.setStep as (s: WizardStep) => void} disabled={isBusy} />
    <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={onClose} disabled={isBusy} aria-label="Close">
      <X className="h-4 w-4" />
    </Button>
  </div>
);

interface FooterProps {
  form: Form;
  onSave: () => Promise<void>;
  onClose: () => void;
  isBusy: boolean;
}

export const SentScheduledWizardFooter: FC<FooterProps> = ({ form, onSave, onClose, isBusy }) => (
  <div className="flex shrink-0 items-center justify-between gap-3 border-t border-border bg-muted/20 px-5 py-3">
    <div>
      {form.step > 1 && (
        <Button variant="outline" size="sm" onClick={form.goBack} disabled={isBusy}>
          <ArrowLeft className="mr-1.5 h-3.5 w-3.5" /> Back
        </Button>
      )}
    </div>
    <div className="flex flex-wrap items-center justify-end gap-2">
      <Button variant="outline" size="sm" onClick={onClose} disabled={isBusy}>
        Cancel
      </Button>
      {form.step < 3 && (
        <Button variant="secondary" size="sm" onClick={onSave} disabled={isBusy || !form.hasAnyChange}>
          {isBusy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1.5 h-3.5 w-3.5" />}
          Save
        </Button>
      )}
      {form.step < 3 ? (
        <Button size="sm" onClick={form.goNext} disabled={isBusy}>
          Next <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
        </Button>
      ) : (
        <Button size="sm" onClick={onSave} disabled={isBusy || !form.hasAnyChange}>
          {isBusy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1.5 h-3.5 w-3.5" />}
          Save all
        </Button>
      )}
    </div>
  </div>
);
